import React, { useState } from "react";
import AdminDataTable from "../AdminDataTable";
import Button from "../../../../components/ui/Button";
import Modal from "../../../../components/ui/Modal";
import { CheckCircle, XCircle, Eye, CreditCard, Banknote } from "lucide-react";
import { useData } from "../../../../context/DataContext";

const PayoutsManager = () => {
  const { payouts, processPayout } = useData();
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const handleOpenAction = (payout) => {
    setSelectedPayout(payout);
    setRejectReason("");
    setIsModalOpen(true);
  };

  const handleProcess = (status) => {
    if (!selectedPayout) return;
    
    // For rejection, require a reason (mock check)
    if (status === "Rejected" && !rejectReason) {
        alert("Please provide a reason for rejection.");
        return;
    }

    processPayout(selectedPayout.id, status, status === "Rejected" ? rejectReason : "Approved by Admin");
    setIsModalOpen(false);
  };

  const columns = [
    {
      header: "Creator",
      key: "creatorName",
      render: (row) => <span className="font-medium text-slate-800">{row.creatorName}</span>
    },
    {
      header: "Amount",
      key: "amount",
      render: (row) => <span className="font-bold text-slate-800">{row.amount.toLocaleString()} ETB</span>
    },
    {
      header: "Method",
      key: "method",
      render: (row) => (
        <div className="flex items-center text-sm text-slate-600">
           <Banknote size={14} className="mr-1.5"/> {row.method}
        </div>
      )
    },
    {
      header: "Requested",
      key: "requestDate",
      render: (row) => <span className="text-sm text-slate-500">{new Date(row.requestDate).toLocaleDateString()}</span>
    },
    {
      header: "Status",
      key: "status",
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
          row.status === "Pending" ? "bg-yellow-100 text-yellow-700" :
          row.status === "Processed" ? "bg-green-100 text-green-700" :
          "bg-red-100 text-red-700"
        }`}>
          {row.status}
        </span>
      )
    },
    {
      header: "Actions",
      key: "actions",
      render: (row) => (
        row.status === "Pending" ? (
            <Button size="sm" variant="outline" onClick={() => handleOpenAction(row)}>
                Process
            </Button>
        ) : (
            <span className="text-xs text-slate-400 italic">No actions</span>
        )
      )
    }
  ];

  const pendingCount = payouts.filter(p => p.status === "Pending").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-700">Payout Requests</h2>
          {pendingCount > 0 && (
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                  {pendingCount} Pending
              </span>
          )}
      </div>

      <AdminDataTable 
        columns={columns} 
        data={payouts}
        searchableFields={["creatorName", "method"]}
        filterableFields={[
            { key: "status", label: "Status", options: ["Pending", "Processed", "Rejected"] }
        ]}
      />

      {/* Action Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title="Process Payout Request"
      >
         {selectedPayout && (
             <div className="space-y-6">
                 <div className="p-4 bg-slate-50 rounded-xl space-y-3 border border-slate-100">
                     <div className="flex justify-between">
                         <span className="text-sm text-slate-500">Creator</span>
                         <span className="font-bold text-slate-800">{selectedPayout.creatorName}</span>
                     </div>
                     <div className="flex justify-between">
                         <span className="text-sm text-slate-500">Amount</span>
                         <span className="font-bold text-2xl text-emerald-600">{selectedPayout.amount.toLocaleString()} ETB</span>
                     </div>
                     <div className="border-t border-slate-200 pt-3">
                         <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Payment Details</div>
                         <div className="flex justify-between items-center text-sm">
                             <span className="text-slate-600">{selectedPayout.method}</span>
                             <span className="font-mono bg-white px-2 py-1 rounded border border-slate-200 select-all">
                                 {selectedPayout.accountNumber}
                             </span>
                         </div>
                     </div>
                 </div>

                 <div>
                     <label className="block text-sm font-medium text-slate-700 mb-2">Rejection Reason (Optional)</label>
                     <textarea 
                        className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-200 text-sm"
                        placeholder="Only required if rejecting the request..."
                        rows={2}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                     />
                 </div>

                 <div className="flex gap-3 pt-2">
                     <button 
                        onClick={() => handleProcess("Rejected")}
                        className="flex-1 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 font-medium transition-colors"
                     >
                         Reject Request
                     </button>
                     <button 
                        onClick={() => handleProcess("Processed")}
                        className="flex-1 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium shadow-sm transition-colors flex justify-center items-center gap-2"
                     >
                         <CheckCircle size={18} /> Approve & Mark Paid
                     </button>
                 </div>
             </div>
         )}
      </Modal>
    </div>
  );
};

export default PayoutsManager;
